import type { PokerGameTile } from '@ps/poker-games-tile-mappers';
import type { Alias } from '@ps/web-core-store';
import type {
  ClubRecentTournPlayerResult,
  ClubRecentTournamentSummary,
  ClubSeasonUserRank,
  HgClubInfo,
  PlayerListElement,
  RecentTournament,
  TableStaticData
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import type {
  ClubMessageData,
  ClubSeason,
  ClubSeasonUserStats,
  HGGetClubTournStructuresReply,
  HgGetClubOptionsReply,
  HgScheduledRingGame,
  HgScheduledTournament,
  ServiceStatusData,
  TableParams
} from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import type { ReactElement } from 'react';

import type { ExtractDispatchFromModel, ExtractSelectFromModel } from '@swf/core/store';
import type { GameView } from '@swf/poker';

import type { WallPosts } from '../components/Club/Wall/utils/normalizeWallPosts';
import type { FormValues } from '../components/CreateGame/utils/common';
import type { HomeGamesWidgetModel } from '../model';

export enum ModalRoutes {
  createClub = '/homegames/clubs/create-a-club',
  createClubSuccess = '/homegames/clubs/create-a-club-success',
  joinClub = '/homegames/clubs/join-a-club',
  createCashGame = '/homegames/create-game/cash-game',
  createCashGameSuccess = '/homegames/create-game/cash-game/success',
  createTournament = '/homegames/create-game/tournament',
  createTournamentSuccess = '/homegames/create-game/tournament/success',
  onBoardingNewUser = '/homegames/onboarding/new-user',
  deleteGamePrompt = '/homegames/prompt/delete-game',
  deleteThreadPrompt = '/homegames/prompt/delete-thread',
  deleteAnnouncementPrompt = '/homegames/prompt/delete-announcement',
  membersAdminActions = '/homegames/members/admin-actions',
  notification = '/homegames/notification'
}

export type HGPokerGameTile = PokerGameTile & {
  supported?: boolean;
  name?: string;
  maxPlayers?: string | number;
  handType?: string;
  potStruct?: string;
  playersSeated?: number;
  entrants?: number;
  isRecurring?: boolean;
  isCashGamesActive?: boolean;
  isTournsActive?: boolean;
};

export interface GamesByClubs {
  [clubId: string]: HGPokerGameTile[];
}

export interface Loading {
  loaderName: string;
  isLoading: boolean;
}

export interface Games {
  recentTourns?: RecentTournament[];
  registeredTourns?: HgScheduledTournament[];
  tables?: TableStaticData[];
  scheduledTables?: HgScheduledRingGame[];
  scheduledTournaments?: HgScheduledTournament[];
  tourns?: RecentTournament[];
  selectedGameIds: string[];
}

export type ClubSeasonStats = Omit<ClubSeasonUserStats, 'rank' | 'knockouts' | 'points'> & {
  rank?: number | string;
  knockouts?: number | string | ReactElement;
  points?: number | string | ReactElement;
};

export interface UILayout {
  clubNavHidden: boolean;
  standingsPlayMoneyFlag: boolean;
}

export interface HomeGamesError {
  critical: boolean;
  major: boolean;
  minor: boolean;
  info: boolean;
}

export interface PageData {
  announcement: (ClubMessageData & { created?: string }) | null;
  clubs: HgClubInfo[];
  games: Games;
  playersByClubId: { [key: string]: PlayerListElement[] };
  playerTournamentResults: ClubRecentTournPlayerResult[];
  clubOptionsById: { [key: string]: HgGetClubOptionsReply };
  clubMembers: {
    approved: PlayerListElement[];
    pending: PlayerListElement[];
    blocked: PlayerListElement[];
  };
  clubSeasonsById: { [key: string]: ClubSeason[] };
  clubSeasonStatsById: { [key: string]: ClubSeasonStats[] };
  clubRecentTournamentSummary: ClubRecentTournamentSummary[];
  loading: { [key: string]: boolean };
  uiLayout: UILayout;
  error: HomeGamesError;
  localStorage: HGLocalStorage;
  serviceStatusData: ServiceStatusData[];
  tableStructures: {
    tableParams: TableParams[];
    initialValues?: FormValues;
  };
  tournamentStructures?: HGGetClubTournStructuresReply;
  wallPosts: WallPosts[];
  wallRanks: ClubSeasonUserRank[];
}

export interface NavLink {
  active?: boolean;
  alias?: Alias;
  values?: { [key: string]: string };
  notification?: {
    visible: boolean;
    content: string;
    variant: 'dot' | 'standard';
  };
  translation: {
    defaultMessage: string;
    id: string;
  };
}

export type InviteLink = 'JOIN_CLUB';

export enum ClubRoute {
  Lobby = 'lobby',
  Results = 'results',
  Members = 'members',
  ReportClub = 'ReportClub',
  Settings = 'settings',
  Standings = 'standings',
  Wall = 'wall'
}

export enum MemberRoute {
  List = 'list',
  Pending = 'pending',
  Blocked = 'blocked'
}

export enum UserClubRole {
  Member,
  Manager,
  Admin,
  ShadowFounder,
  Observer,
  Confirmation,
  Guest,
  Suspended
}

export enum ClubPlayerStatus {
  APPROVED = 'HG_MEMBERSHIP_STATUS_APPROVED',
  APPLIED = 'HG_MEMBERSHIP_STATUS_APPLIED',
  REJECTED = 'HG_MEMBERSHIP_STATUS_REJECTED',
  BLOCKED = 'HG_MEMBERSHIP_STATUS_BLOCKED',
  SUSPENDED = 'HG_MEMBERSHIP_STATUS_SUSPENDED',
  RESIGNED = 'HG_MEMBERSHIP_STATUS_RESIGNED',
  GUEST = 'HG_MEMBERSHIP_STATUS_GUEST',
  BANNED = 'HG_MEMBERSHIP_STATUS_BANNED'
}

export enum ClubPlayerRole {
  MEMBER = 'HG_MEMBER_ROLE_MEMBER',
  FOUNDER = 'HG_MEMBER_ROLE_FOUNDER',
  ADMIN = 'HG_MEMBER_ROLE_ADMIN',
  SHADOW_FOUNDER = 'HG_MEMBER_ROLE_SHADOW_FOUNDER',
  OBSERVER = 'HG_MEMBER_ROLE_OBSERVER',
  APPLIED = 'HG_MEMBER_ROLE_APPLIED',
  GUEST = 'HG_MEMBER_ROLE_GUEST',
  SUSPENDED = 'HG_MEMBER_ROLE_SUSPENDED'
}

export enum HGGameType {
  CashGame = 'Cash Game',
  Tournament = 'Tournament'
}

export enum TileType {
  Cash = 'Cash',
  Upcoming = 'Upcoming',
  Registered = 'Registered',
  Recent = 'Recent'
}

export enum HGOnboarding {
  CreateCashGame = 'createCashGame',
  CreateTournament = 'createTournament',
  CreatedClub = 'createdClub',
  NewClubCreateGame = 'newClubCreateGame',
  CreatedGame = 'createdGame',
  NewClubCreateGameReminder = 'newClubCreateGameReminder',
  NewUserFlow = 'newUserFlow',
  JoinClub = 'joinClub'
}

export enum HGToastType {
  MakeClubPublicToast = 'MakeClubPublicToast',
  JoinAndCreateClubToast = 'JoinAndCreateClubToast'
}

export interface HGLocalStorage {
  gameTileView?: { [key: string]: GameView };
  onboarding?: {
    [K in HGOnboarding]?: boolean;
  };
  toastMessage?: {
    [K in HGToastType]?: { isHidden: boolean };
  };
}

export enum GameTypes {
  'None' = 0,
  'Badugi' = 1,
  'Holdem' = 2,
  '5 Omaha' = 3,
  'Omaha' = 4,
  'Draw' = 5,
  'Single Draw 27' = 6,
  'Stud' = 7,
  'Razz' = 8,
  '3 Draw 27' = 9,
  '3 Draw A5' = 10,
  'Courchevel' = 11,
  '6 Omaha' = 12,
  'Split Holdem' = 13,
  'Split Omaha' = 14,
  'Showtime Holdem' = 15,
  'Showtime Omaha' = 16,
  'Fusion' = 17,
  'Unfold Classic' = 18,
  'Unfold Max' = 19,
  '6+ Holdem' = 20,
  'Deep Water Holdem' = 21,
  'Deep Water Omaha' = 22,
  'Swap Holdem' = 23,
  'Tempest Holdem' = 24
}

export interface PageProps {
  data: PageData;
}

export interface CreateModalLocationState {
  widgetSelect: ExtractSelectFromModel<HomeGamesWidgetModel>;
  widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel>;
}
